import { useState, useCallback, useEffect } from 'react';
import { validateGameIntervals } from '@/modules/game/validators';
import { eventBus } from '@/modules/core/events';
import { events } from '@/modules/game/events';
import {
  loadLiveMatchGame,
  saveLiveMatchGame,
  clearLiveMatch,
} from '@/modules/game/utils/liveMatchStorage';

/**
 * Internal state management for game module.
 *
 * Game state is restored from the live-match snapshot on boot (so a refresh
 * mid-match loses nothing — scores, goals, timer intervals) and
 * re-snapshotted on every change.
 */
export function useGameState() {
  const [restored] = useState(() => loadLiveMatchGame());

  const [isRunning, setIsRunning] = useState(restored?.isRunning ?? false);
  const [gameIntervals, setGameIntervals] = useState(restored?.gameIntervals ?? []);
  const [ourScore, setOurScore] = useState(restored?.ourScore ?? 0);
  const [opponentScore, setOpponentScore] = useState(restored?.opponentScore ?? 0);
  const [goals, setGoals] = useState(restored?.goals ?? []);
  const [goalkeeper, setGoalkeeper] = useState(restored?.goalkeeper ?? null);
  const [includeGKPlaytime, setIncludeGKPlaytime] = useState(restored?.includeGKPlaytime ?? true);

  // Snapshot on every change — cheap because game state only changes on
  // discrete events (start/pause, goals, subs syncing intervals).
  useEffect(() => {
    saveLiveMatchGame({
      isRunning,
      gameIntervals,
      ourScore,
      opponentScore,
      goals,
      goalkeeper,
      includeGKPlaytime,
    });
  }, [isRunning, gameIntervals, ourScore, opponentScore, goals, goalkeeper, includeGKPlaytime]);
  
  // Update game intervals with validation
  const updateGameIntervals = useCallback((newIntervals) => {
    try {
      // Handle the case where newIntervals is a function
      const intervalsToValidate = typeof newIntervals === 'function' 
        ? newIntervals(gameIntervals)
        : newIntervals;
      
      const validatedIntervals = validateGameIntervals(intervalsToValidate, {
        actionName: 'updateGameIntervals',
        location: 'game/internal/state.js:updateGameIntervals',
        direction: 'internal',
        moduleFrom: 'game',
        moduleTo: 'game'
      });
      
      setGameIntervals(validatedIntervals);
      return validatedIntervals;
    } catch (error) {
      console.error('Game intervals validation error:', error);
      return gameIntervals;
    }
  }, [gameIntervals]);
  
  // Reset the game state
  const resetGame = useCallback(() => {
    setIsRunning(false);
    setGameIntervals([]);
    setOurScore(0);
    setOpponentScore(0);
    setGoals([]);
    setGoalkeeper(null);
    setIncludeGKPlaytime(true);

    // No live match anymore — drop the snapshot so /manage can never
    // resurrect a finished match after a refresh.
    clearLiveMatch();

    eventBus.publish(events.GAME_RESET, { timestamp: Date.now() });
  }, []);
  
  return {
    // Basic state
    isRunning,
    setIsRunning,
    gameIntervals,
    setGameIntervals: updateGameIntervals,
    
    // Score tracking
    ourScore,
    setOurScore,
    opponentScore,
    setOpponentScore,
    goals,
    setGoals,
    
    // Goalkeeper tracking
    goalkeeper,
    setGoalkeeper,
    includeGKPlaytime,
    setIncludeGKPlaytime,
    
    // Actions
    resetGame
  };
}